import React from 'react';
import { Modal, LockboxModalHeader, Button } from '../ui';
import { zIndex } from '../../design-system/tokens';
import { PLATFORM_FEE_DISCLOSURE } from '../../constants/productCopy';

const STEPS = [
  { n: 1, title: 'Create a free account', body: 'Sign up to fund your wallet, challenge other players, and claim challenge earnings. Guests can browse only.' },
  { n: 2, title: 'Add funds to your wallet', body: 'Deposit securely through Stripe. Wallet funds are used for challenge entries — not subscriptions.' },
  { n: 3, title: 'Challenge another player', body: 'Pick a live game matchup or create a custom challenge with someone on your squad.' },
  { n: 4, title: 'Funds move to locked funds', body: 'Accepted challenge amounts are held as locked funds until the challenge settles. Neither player can access locked funds early.' },
  { n: 5, title: 'The challenge settles', body: 'Live games resolve from final scores. Custom challenges use agreed outcomes between both players.' },
  { n: 6, title: 'Winner claims earnings', body: 'After settlement, the winner reviews the transfer breakdown and claims challenge earnings to their wallet.' },
  { n: 7, title: 'Withdraw when eligible', body: 'Connect your bank through Stripe and withdraw available wallet balance when you are ready.' },
];

/** How LockedIn works — presentation only; open/close controlled by App.js. */
export default function HowToPlayModal({ open, onClose }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      accent="brandStrong"
      size="lg"
      z={zIndex.modalHigh}
      bodyClassName="p-6 space-y-6"
      header={(
        <LockboxModalHeader
          emoji="📖"
          iconClassName="bg-emerald-500/20 border-emerald-500/30"
          title="How LockedIn works"
          subtitle="Peer-to-peer challenge marketplace"
          onClose={onClose}
        />
      )}
      footer={(
        <Button type="button" fullWidth onClick={onClose}>
          Got it
        </Button>
      )}
    >
      <section className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
        <p className="text-sm text-gray-300 leading-relaxed">
          LockedIn is a peer-to-peer sports challenge marketplace where users create and accept
          head-to-head challenges with other users. Accepted challenge amounts move to locked funds
          until settlement, then the winner receives the challenge pool minus the platform service fee.{' '}
          <strong className="text-white">{PLATFORM_FEE_DISCLOSURE}</strong>
        </p>
        <p className="text-xs text-gray-500 mt-3 font-medium">
          Subscription payments unlock features and reduced fees. They do not add to your wallet balance.
        </p>
      </section>

      <ol className="space-y-4">
        {STEPS.map((step) => (
          <li key={step.n} className="flex gap-4">
            <span className="shrink-0 w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center text-sm font-black text-emerald-400">
              {step.n}
            </span>
            <div>
              <h3 className="text-sm font-black text-white mb-1">{step.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{step.body}</p>
            </div>
          </li>
        ))}
      </ol>

      <section className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4">
        <h3 className="text-xs font-black uppercase tracking-widest text-amber-400 mb-2">Disputes</h3>
        <p className="text-sm text-gray-400 leading-relaxed">
          Custom challenges include a second-chance vote if players disagree. Unresolved disputes
          trigger a 24-hour locked-funds hold and account safeguards. Three strikes may result in a
          30-day suspension from creating challenges.
        </p>
      </section>
    </Modal>
  );
}
